import { useEffect } from "react";

type SeoOptions = {
  title: string;
  description: string;
  keywords: string;
  path: string;
  type?: string;
  image?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

const SITE_NAME = "黑料剧场";

const setNamedMeta = (attr: "name" | "property", key: string, value: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};

/**
 * 统一管理页面级 SEO：标题、描述、关键词、Canonical、Robots、OG / Twitter 与 JSON-LD。
 */
export default function useSeo(options: SeoOptions) {
  const { title, description, keywords, path, type = "website", image, jsonLd } = options;

  useEffect(() => {
    const base = (typeof __BASE_PATH__ !== "undefined" ? __BASE_PATH__ : "/").replace(/\/+$/, "");
    const origin = window.location.origin;
    const canonical = `${origin}${base}${path === "/" ? "/" : path}`;
    const shareImage =
      image ||
      "https://readdy.ai/api/search-image?query=Dark%20cinematic%20theater%20stage%20with%20deep%20crimson%20curtains%20and%20a%20warm%20gold%20spotlight%20beam%20through%20atmospheric%20haze%2C%20elegant%20luxurious%20moody%20background%2C%20film%20grain%2C%20high%20detail%2C%20no%20text&width=1200&height=630&seq=hl-og-cover&orientation=landscape";

    document.title = title;

    setNamedMeta("name", "description", description);
    setNamedMeta("name", "keywords", keywords);
    setNamedMeta("name", "robots", "index, follow, max-image-preview:large");
    setNamedMeta("name", "author", SITE_NAME);

    let canonicalLink = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", canonical);

    setNamedMeta("property", "og:site_name", SITE_NAME);
    setNamedMeta("property", "og:title", title);
    setNamedMeta("property", "og:description", description);
    setNamedMeta("property", "og:type", type);
    setNamedMeta("property", "og:url", canonical);
    setNamedMeta("property", "og:image", shareImage);
    setNamedMeta("property", "og:locale", "zh_CN");

    setNamedMeta("name", "twitter:card", "summary_large_image");
    setNamedMeta("name", "twitter:title", title);
    setNamedMeta("name", "twitter:description", description);
    setNamedMeta("name", "twitter:image", shareImage);

    const scriptId = "page-jsonld";
    const old = document.getElementById(scriptId);
    if (old) old.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = scriptId;
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, path, type, image, jsonLd]);
}